<template>
  <!-- Year Navigation -->
  <div class="d-flex justify-content-center align-items-center mb-4 year-navigation">
    <button class="btn btn-link me-3" @click="changeYear('previous')">
      <i class="bi bi-chevron-left"></i>
    </button>
    <h4 class="year-title">{{ availableYears[currentYearIndex].year }}</h4>
    <button class="btn btn-link ms-3" @click="changeYear('next')">
      <i class="bi bi-chevron-right"></i>
    </button>
  </div>

  <!-- Calendar Section -->
  <div class="col-md-12">
    <div class="row calendar-grid">
      <div v-for="month in months" :key="month.value" class="col-md-4 calendar-column">
        <div class="calendar-card">
          <h5 class="text-center calendar-month-title">{{ month.name }}</h5>
          <table class="table calendar-table">
            <thead>
              <tr>
                <th v-for="day in weekDays" :key="day">{{ day }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="week in getMonthCalendar(month.value)" :key="week[0]?.date">
                <td v-for="day in week" :key="day.date" :class="[getDayClass(day), { selected: isSelected(day) }]"
                  :style="getDayStyle(day)" :title="getHolidayTooltip(day) || getAbsenceTooltip(day)"
                  @click="toggleDaySelection(day, $event)">
                  {{ day.day }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <!-- Floating Restriction Tooltip -->
  <Transition name="fade">
    <div v-if="restrictionTooltip.show" class="restriction-popup"
      :style="{ top: restrictionTooltip.y + 'px', left: restrictionTooltip.x + 'px' }">
      <i class="bi bi-info-circle me-1"></i>
      {{ restrictionTooltip.text }}
    </div>
  </Transition>
</template>

<script>
import { useMainStore } from "@/store";
import { eachDayOfInterval, endOfMonth, endOfWeek, format, getDay, startOfMonth, startOfWeek } from "date-fns";

export default {
  name: "YearlyCalendar",
  props: {
    selectedDays: {
      type: Array,
      default: () => [],
    },
    absences: {
      type: Array,
      default: () => [],
      required: true,
    },
    holidays: {
      type: Array,
      default: () => [],
      required: true,
    },
    availableYears: {
      type: Array,
      default: () => [],
      required: true,
    },
    currentYearIndex: {
      type: Number,
      required: true,
    },
    selectedUsers: {
      type: Array,
      default: () => [],
    },
    selectedAbsenceType: {
      type: Object,
      required: false,
    },
    restrictedDates: {
      type: Array,
      default: () => [],
    },
    enableTwoWeekRule: {
      type: Boolean,
      default: false,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["change-year", "update:selectedDays"],
  data() {
    return {
      internalSelectedDays: [],
      restrictionTooltip: {
        show: false,
        text: '',
        x: 0,
        y: 0
      }
    };
  },
  computed: {
    store: () => useMainStore(),
    months() {
      const monthNames = this.$tm("months");
      return Array.isArray(monthNames)
        ? monthNames.map((name, index) => ({ name, value: index + 1 }))
        : [];
    },
    weekDays() {
      const weekDayNames = this.$tm("weekdays");
      return Array.isArray(weekDayNames) ? weekDayNames : [];
    },
    restrictedSet() {
      return new Set(this.restrictedDates || []);
    },
    isActingAsAdmin() {
      return this.store.user.permissions.includes("users.manage_all_absences") && !!this.$route.query.user_ids;
    },
  },
  watch: {
    selectedDays: {
      immediate: true,
      handler(newVal) {
        this.internalSelectedDays = [...newVal];
      },
    },
  },
  methods: {
    changeYear(direction) {
      let newIndex = this.currentYearIndex;

      if (direction === "previous" && newIndex > 0) newIndex--;
      else if (direction === "next" && newIndex < this.availableYears.length - 1) newIndex++;

      this.$emit("change-year", newIndex);
    },

    // --- helpers ----------------------------------------------------------
    getTodayISO() {
      // local "today" in yyyy-MM-dd
      return format(new Date(), "yyyy-MM-dd");
    },

    isSameUser(absence) {
      const currentUserId = Number(this.store.user.id || localStorage.getItem("userID"));
      if (!absence || absence.user == null) return false;

      if (typeof absence.user === "object") {
        return Number(absence.user.id) === currentUserId;
      }
      return Number(absence.user) === currentUserId;
    },

    isDeletableApprovedAbsence(absence) {
      if (!absence) return false;

      if (this.isActingAsAdmin) return true;

      // Normal user: only their own approved absences in the future
      if (!this.isSameUser(absence)) return false;

      const today = this.getTodayISO();
      return absence.absence_date > today; // strictly future
    },

    // --- selection --------------------------------------------------------
    toggleDaySelection(day, event) {
      // skip non-interactive days first
      if (
        !day.isCurrentMonth ||
        ["holiday", "weekend", "disabled-date"].includes(this.getDayClass(day)) ||
        (this.isReadOnly && !this.isActingAsAdmin)
      ) {
        // Show restriction reason if it's a blocked date or year is closed
        if (day.isCurrentMonth) {
          this.showRestrictionTooltip(event, day);
        }
        return;
      }

      const formattedDate = format(day.date, "yyyy-MM-dd");
      const existingAbsence = this.absences.find(a => a.absence_date === formattedDate);

      const isPendingAbsence = existingAbsence?.status === "pending";
      const isApprovedAbsence = existingAbsence?.status === "approved";
      const isDeclinedAbsence = existingAbsence?.status === "declined";

      // Declined absences should not be interactive (even if present in data)
      if (isDeclinedAbsence) return;

      if (this.selectedAbsenceType) {
        const restricted = this.isDayRestricted(day) ||
          (this.selectedAbsenceType.category === 'VAC' && this.isBeforeMinDate(day));
        if (restricted) {
          this.showRestrictionTooltip(event, day);
          return;
        }
      }

      // Allow selection if:
      // - user is creating new absences (absence type selected), OR
      // - selecting existing pending absences, OR
      // - selecting existing approved absences that are deletable (future self-approved or admin)
      const allowSelection =
        !!this.selectedAbsenceType ||
        isPendingAbsence ||
        (isApprovedAbsence && this.isDeletableApprovedAbsence(existingAbsence));

      if (!allowSelection) return;

      const index = this.internalSelectedDays.indexOf(formattedDate);
      if (index !== -1) {
        this.internalSelectedDays.splice(index, 1);
      } else {
        this.internalSelectedDays.push(formattedDate);
      }

      this.$emit("update:selectedDays", [...this.internalSelectedDays]);
    }
    ,

    isSelected(day) {
      const formattedDate = format(day.date, "yyyy-MM-dd");
      return this.internalSelectedDays.includes(formattedDate);
    }
    ,

    // --- restrictions -----------------------------------------------------
    isDayRestricted(day) {
      const formattedDate = format(day.date, "yyyy-MM-dd");

      const isWeekend = getDay(day.date) === 6 || getDay(day.date) === 0;
      const isHoliday = this.holidays?.some(h => h.date === formattedDate);
      const isRestricted = this.restrictedDates?.includes(formattedDate) && this.selectedAbsenceType?.category === 'VAC';

      // privileged users (acting as admin): no restriction
      if (this.isActingAsAdmin) return false;

      const absence = this.absences?.find(a => a.absence_date === formattedDate);

      // if day already has an absence, allow interacting with it (selection handled elsewhere)
      if (absence) return false;

      return isWeekend || isHoliday || isRestricted;
    }
    ,

    isBeforeMinDate(day) {
      if (!this.enableTwoWeekRule) return false;

      if (this.isActingAsAdmin) return false;

      return day.date < this.getMinSelectableDate();
    }
    ,

    getMinSelectableDate() {
      const today = new Date();

      if (!this.enableTwoWeekRule) {
        return today;
      }
      const d = new Date();
      d.setDate(today.getDate() + 14);
      return d;
    }
    ,

    getDayClass(day) {
      const formattedDate = format(day.date, "yyyy-MM-dd");
      const absence = this.absences.find(a => a.absence_date === formattedDate);
      const isWeekend = getDay(day.date) === 6 || getDay(day.date) === 0;
      const holiday = this.holidays.find(h => h.date === formattedDate);

      const isRestricted = (this.isReadOnly && !this.isActingAsAdmin) || (this.selectedAbsenceType && (
        this.isDayRestricted(day) ||
        // 14 days rull
        (this.selectedAbsenceType.category === 'VAC' && this.isBeforeMinDate(day))
      ));

      if (!day.isCurrentMonth) return "inactive";

      if (absence) {
        if (absence.status === "pending") return "pending-absence";
        if (absence.status === "approved") return "approved-absence";
        if (absence.status === "declined") return "declined-absence";
      }

      if (isRestricted) return "disabled-date";
      if (holiday) return "holiday";
      if (isWeekend) return "weekend";
      return "";
    }
    ,

    getDayStyle(day) {
      const formattedDate = format(day.date, "yyyy-MM-dd");
      const absence = this.absences.find(a => a.absence_date === formattedDate);

      // Existing absence (saved on server)
      if (absence) {
        return {
          backgroundColor: absence.absence_type?.color_code,
          opacity: absence.status === "pending" ? 0.5 : 1,
        };
      }

      // Newly selected day (not yet saved)
      if (this.isSelected(day) && this.selectedAbsenceType) {
        return {
          backgroundColor: this.selectedAbsenceType.color_code,
          opacity: 0.6,
        };
      }

      return {};
    }
    ,

    getMonthCalendar(month) {
      const year = this.availableYears[this.currentYearIndex].year;
      const monthStart = startOfMonth(new Date(year, month - 1));
      const monthEnd = endOfMonth(new Date(year, month - 1));
      const weeks = [];
      let week = [];

      eachDayOfInterval({
        start: startOfWeek(monthStart, { weekStartsOn: 1 }),
        end: endOfWeek(monthEnd, { weekStartsOn: 1 }),
      }).forEach((date) => {
        week.push({
          date,
          day: format(date, "d"),
          isCurrentMonth: date >= monthStart && date <= monthEnd,
        });

        if (week.length === 7) {
          weeks.push(week);
          week = [];
        }
      });

      return weeks;
    }
    ,

    getHolidayTooltip(day) {
      const formattedDate = format(day.date, "yyyy-MM-dd");
      const holiday = this.holidays.find(h => h.date === formattedDate);
      return holiday ? holiday.name : "";
    }
    ,

    getAbsenceTooltip(day) {
      if (!this.absences?.length) return "";

      const dateStr = format(day.date, "yyyy-MM-dd");
      const matching = this.absences.filter(a => a.absence_date === dateStr);

      if (!matching.length) return "";

      return matching
        .map(a => {
          const u = a.user;
          const userLabel =
            typeof u === "object" ? `${u.first_name} ${u.last_name}` : "User";
          return `${userLabel} (${a.absence_type?.name || ""})`;
        })
        .join("\n");
    },

    showRestrictionTooltip(event, day) {
      const reason = this.getRestrictionReason(day);
      if (!reason) return;

      this.restrictionTooltip.text = reason;
      this.restrictionTooltip.x = event.clientX + 10;
      this.restrictionTooltip.y = event.clientY + 10;
      this.restrictionTooltip.show = true;

      // Auto-hide after 2 seconds
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = setTimeout(() => {
        this.restrictionTooltip.show = false;
      }, 2500);
    },

    getRestrictionReason(day) {
      const formattedDate = format(day.date, "yyyy-MM-dd");

      // 1. Check for specific holiday
      const holiday = this.holidays.find(h => h.date === formattedDate);
      if (holiday) return holiday.name;

      // 2. Check for weekend
      if (getDay(day.date) === 6 || getDay(day.date) === 0) {
        return this.$t('calendarHelpWeekend') || "Weekend";
      }

      // 3. Check for company restricted dates
      if (this.restrictedDates?.includes(formattedDate)) {
        return this.$t('calendarHelpRestricted') || "Date restricted by company";
      }

      // 4. Check for 14-days rule
      if (this.selectedAbsenceType?.category === 'VAC' && this.isBeforeMinDate(day)) {
        return this.$t('calendarHelpMinDays') || "Minimum 14 days notice required";
      }

      // 5. Check for closed year
      if (this.isReadOnly && !this.isActingAsAdmin) {
        return this.$t('calendarHelpYearClosed') || "This year is closed for modifications";
      }

      return "";
    },
  },
};
</script>

<style scoped>
.disabled-date {
  color: grey;
  font-weight: bold;
  background-color: #f0f0f0 !important;
  cursor: not-allowed;
}

.restriction-popup {
  position: fixed;
  background: #5c5c5c;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  z-index: 9999;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  max-width: 250px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
